// ============================================================================
// 릴리즈 + 워크플로우 API 서비스 (CHG-048)
// ============================================================================

import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Release Types ---

export interface ReleaseListItem {
  releaseId: string;
  version: string;
  title: string;
  status: string;
  targetDate: string | null;
  releasedAt: string | null;
  createdBy: { id: string; name: string };
  createdAt: string;
  issueCount: number;
  completionPercent: number;
}

export interface ReleaseDetail {
  releaseId: string;
  version: string;
  title: string;
  description: string | null;
  status: string;
  targetDate: string | null;
  releasedAt: string | null;
  githubReleaseUrl: string | null;
  createdBy: { id: string; name: string };
  issues: {
    issueId: string;
    issueKey: string;
    title: string;
    status: string;
    statusCategory: string;
    statusColor: string;
    assignee: { id: string; name: string } | null;
    prCount: number;
    prMerged: number;
    ciStatus: string | null;
    testCount: number;
    testPass: number;
  }[];
  readiness: { totalIssues: number; completed: number; inProgress: number; notStarted: number; completionPercent: number };
  blockers: { issueKey: string; type: string; message: string }[];
  prSummary: { total: number; merged: number; open: number };
  testSummary: { total: number; pass: number; fail: number; notRun: number };
  designSummary?: {
    totalIssues: number;
    issuesWithDesign: number;
    designCoveragePercent: number;
    statusBreakdown: Record<string, number>;
    blockers: { issueKey: string; blockerType: string; detail: string }[];
  };
}

export interface WorkflowRunItem {
  runId: string;
  workflowName: string;
  runNumber: number;
  githubRunId: string;
  status: string;
  conclusion: string | null;
  htmlUrl: string;
  durationSeconds: number | null;
  triggerEvent: string;
  headBranch: string;
  createdAt: string;
  jobs: { jobId: number; name: string; status: string; conclusion: string | null; durationSeconds: number | null }[];
  repo: { fullName: string };
  pullRequest: { prId: string; prNumber: number; title: string } | null;
}

// --- Release API ---

export async function fetchReleases(projectId: string, status?: string): Promise<ReleaseListItem[]> {
  const params = status ? { status } : undefined;
  const res = await api.get<ApiResponse<ReleaseListItem[]>>(`/projects/${projectId}/releases`, { params });
  return res.data.data!;
}

export async function fetchReleaseDetail(projectId: string, releaseId: string): Promise<ReleaseDetail> {
  const res = await api.get<ApiResponse<ReleaseDetail>>(`/projects/${projectId}/releases/${releaseId}`);
  return res.data.data!;
}

export async function createRelease(projectId: string, data: { version: string; title: string; description?: string; targetDate?: string }) {
  const res = await api.post<ApiResponse<ReleaseListItem>>(`/projects/${projectId}/releases`, data);
  return res.data.data!;
}

export async function updateRelease(projectId: string, releaseId: string, data: Record<string, unknown>) {
  const res = await api.put<ApiResponse>(`/projects/${projectId}/releases/${releaseId}`, data);
  return res.data;
}

export async function addReleaseIssues(
  projectId: string,
  releaseId: string,
  issueIds: string[],
  options?: { includeDescendants?: boolean },
) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/releases/${releaseId}/issues`, {
    issueIds,
    includeDescendants: options?.includeDescendants,
  });
  return res.data;
}

export async function removeReleaseIssue(projectId: string, releaseId: string, issueId: string) {
  await api.delete(`/projects/${projectId}/releases/${releaseId}/issues/${issueId}`);
}

// 이슈에 연결된 릴리즈 목록
export interface IssueLinkedRelease {
  releaseId: string;
  version: string;
  title: string;
  status: string;
  targetDate: string | null;
  releasedAt: string | null;
}

export async function fetchIssueReleases(projectId: string, issueId: string): Promise<IssueLinkedRelease[]> {
  const res = await api.get<ApiResponse<IssueLinkedRelease[]>>(
    `/projects/${projectId}/issues/${issueId}/releases`,
  );
  return res.data.data ?? [];
}

// --- CHG-100: 릴리즈 TC 관리 API ---

export interface ReleaseTestMetrics {
  totalTestCases: number;
  passCount: number;
  failCount: number;
  skipCount: number;
  notExecutedCount: number;
  coverageRate: number;
  passRate: number;
  byIssue: ReleaseIssueTestInfo[];
}

export interface ReleaseIssueTestInfo {
  issueId: string;
  issueKey: string;
  issueTitle: string;
  totalTc: number;
  pass: number;
  fail: number;
  skip: number;
  notExecuted: number;
  testCases: ReleaseTestCaseInfo[];
}

export interface ReleaseTestCaseInfo {
  testCaseId: string;
  tcId: string;
  title: string;
  precondition: string | null;
  steps: { step?: number; action: string; expected?: string }[];
  expectedResult: string | null;
  source: 'AUTO_DISCOVERED' | 'MANUAL';
  latestStatus: string;
  executedAt: string | null;
  executedBy: { id: string; name: string } | null;
}

export async function fetchReleaseTestMetrics(projectId: string, releaseId: string): Promise<ReleaseTestMetrics> {
  const res = await api.get<ApiResponse<ReleaseTestMetrics>>(`/projects/${projectId}/releases/${releaseId}/test-metrics`);
  return res.data.data!;
}

export async function rescanTestCases(projectId: string, releaseId: string) {
  const res = await api.post<ApiResponse<{ scanned: number; newlyLinked: number }>>(`/projects/${projectId}/releases/${releaseId}/rescan-test-cases`);
  return res.data.data!;
}

export async function createReleaseTestCase(projectId: string, releaseId: string, issueId: string, data: {
  title: string; type?: string; precondition?: string; expectedResult?: string; steps?: { action: string }[]; priority?: string;
}) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/releases/${releaseId}/issues/${issueId}/test-cases`, data);
  return res.data.data;
}

export async function linkTestCases(projectId: string, releaseId: string, issueId: string, testCaseIds: string[]) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/releases/${releaseId}/issues/${issueId}/link-test-cases`, { testCaseIds });
  return res.data.data;
}

export async function unlinkTestCase(projectId: string, releaseId: string, issueId: string, testCaseId: string) {
  await api.delete(`/projects/${projectId}/releases/${releaseId}/issues/${issueId}/test-cases/${testCaseId}`);
}

export async function executeReleaseTest(projectId: string, releaseId: string, data: {
  testCaseId: string; issueId: string; status: 'PASS' | 'FAIL' | 'SKIP'; actualResult?: string;
}) {
  const res = await api.post<ApiResponse>(`/projects/${projectId}/releases/${releaseId}/test-executions`, data);
  return res.data.data;
}

// --- Workflow API ---

export async function fetchIssueWorkflows(projectId: string, issueId: string): Promise<WorkflowRunItem[]> {
  const res = await api.get<ApiResponse<WorkflowRunItem[]>>(`/projects/${projectId}/github/workflows/issues/${issueId}`);
  return res.data.data!;
}
